import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { theme } from '../theme';
import type { AuthUser } from '../context/AuthContext';
import ParticleCanvas from '../components/ParticleCanvas';

export function LoginPage() {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  async function sendOtp() {
    setErr('');
    setBusy(true);
    try {
      await api.post('/auth/send-otp', { mobile });
      setSent(true);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (!e.response) {
          setErr(
            `Cannot reach the API (${String(api.defaults.baseURL)}). Start the ODC backend on port 5002, keep "proxy" in frontend/package.json as http://localhost:5002, run npm start from the frontend folder, and open the app at the same host/port (localhost or 127.0.0.1) as shown in the terminal. ${e.message}`
          );
        } else {
          const m = (e.response.data as { error?: string } | undefined)?.error;
          setErr(m || `Server error (${e.response.status})`);
        }
      } else {
        setErr('Could not send OTP');
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setErr('');
    setBusy(true);
    const code = otp.join('');
    try {
      const { data } = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: AuthUser;
      }>('/auth/verify-otp', { mobile, otp: code });
      login(data.accessToken, data.refreshToken, data.user);
      try {
        const me = await api.get<{ business: unknown }>('/business/me');
        nav(me.data.business ? '/dashboard' : '/onboarding', { replace: true });
      } catch {
        nav('/onboarding', { replace: true });
      }
    } catch (e: unknown) {
      const m = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErr(m || 'Invalid OTP');
    } finally {
      setBusy(false);
    }
  }

  async function handleSendOtp() {
    await sendOtp();
  }

  async function handleVerifyOtp() {
    await verify();
  }

  function otpBox(i: number, v: string) {
    return (
      <input
        key={i}
        maxLength={1}
        className="odc-input !w-11 !px-0 !py-3 text-center text-lg font-bold sm:!w-12"
        style={{ borderRadius: 14 }}
        value={v}
        onChange={(e) => {
          const d = e.target.value.replace(/\D/g, '');
          const next = [...otp];
          next[i] = d.slice(-1);
          setOtp(next);
          if (d && i < 5) {
            const el = document.getElementById(`otp-${i + 1}`);
            (el as HTMLInputElement | null)?.focus();
          }
        }}
        id={`otp-${i}`}
        inputMode="numeric"
        autoComplete="one-time-code"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleVerifyOtp();
          }
        }}
      />
    );
  }

  return (
    <div className="relative flex min-h-screen">
      <div className="odc-grid-bg" aria-hidden />
      <div
        className="relative hidden w-1/2 flex-col justify-center overflow-hidden p-12 lg:flex"
        style={{ background: 'var(--bg2)' }}
      >
        <ParticleCanvas />
        <div className="relative z-10">
          <span className="text-5xl odc-float">🤖</span>
          <h1
            className="mt-6 text-4xl font-extrabold"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: 'var(--text)' }}
          >
            OnlineDigitalCares
          </h1>
          <p className="mt-4 max-w-sm" style={{ color: 'var(--muted)' }}>
            Powerful AI agents. One login. OTP-secured access — no passwords to forget.
          </p>
          <p className="mt-6 text-lg font-semibold" style={{ color: theme.primary }}>
            "Sleep well — ODC never stops working!" 🌙
          </p>
        </div>
      </div>
      <div className="relative z-10 flex flex-1 flex-col justify-center px-6 py-12" style={{ background: 'var(--bg)' }}>
        <div className="odc-page mx-auto w-full max-w-md">
          <Link to="/" className="text-sm transition hover:opacity-80" style={{ color: 'var(--muted)' }}>
            ← Back
          </Link>
          <h2 className="mt-6 text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Sign in with OTP
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
            We will text a 6-digit code to your number.
          </p>

          <label className="mt-8 block text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
            Mobile
          </label>
          <input
            className="odc-input mt-2"
            placeholder="9876543210"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSendOtp();
              }
            }}
            inputMode="tel"
          />
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Press Enter to continue</p>

          {!sent ? (
            <button
              type="button"
              disabled={busy}
              className="odc-btn-primary mt-6 w-full justify-center !py-3.5 disabled:opacity-60"
              onClick={() => void sendOtp()}
            >
              Send OTP
            </button>
          ) : (
            <>
              <p className="mt-8 text-xs font-semibold uppercase" style={{ color: 'var(--muted)' }}>
                Enter OTP
              </p>
              <div className="mt-3 flex justify-center gap-2 sm:gap-3">{otp.map((v, i) => otpBox(i, v))}</div>
              <p className="mt-2 text-center" style={{ fontSize: 11, color: 'var(--muted)' }}>
                Press Enter to continue
              </p>
              <button
                type="button"
                disabled={busy}
                className="odc-btn-primary mt-8 w-full justify-center !py-3.5 disabled:opacity-60"
                onClick={() => void handleVerifyOtp()}
              >
                Verify & Login
              </button>
            </>
          )}

          {err ? (
            <p className="mt-4 text-sm" style={{ color: theme.danger }}>
              {err}
            </p>
          ) : null}

          <Link
            to="/faq"
            className="mt-6 inline-block text-center text-xs"
            style={{ color: 'var(--muted)', textDecoration: 'none', width: '100%' }}
          >
            Need help? View FAQ →
          </Link>
        </div>
      </div>
    </div>
  );
}
